/**
 * Release decision (F7.6-c). uc-core ReleaseDecisionDto + ReleaseService.decide:
 *   decision RELEASE | HOLD; reason required for HOLD (≥ 10 chars, ≤ 200); attested = true;
 *   reauthToken from the signer's own password. RELEASE also needs every checklist line green and a
 *   signer who neither ran nor cleaned the pack line (R11, 403 DECIDER_NOT_ALLOWED).
 */
import { checklistLabel } from './labText'

export const HOLD_REASON_MIN = 10
export const HOLD_REASON_MAX = 200

export const OWN_RUN_REASON = 'You ran or cleaned this line; another person must release it.'
export const NOT_GREEN_REASON = 'Every checklist line must be green to release.'

export type ReleaseDecision = 'RELEASE' | 'HOLD'

export interface ReleaseDecisionDraft {
  decision: ReleaseDecision
  reason: string
  attested: boolean
  password: string
}

export interface ReleaseDecisionProblems {
  decision?: string
  reason?: string
  attested?: string
  password?: string
}

export const emptyReleaseDecision = (decision: ReleaseDecision = 'RELEASE'): ReleaseDecisionDraft => ({
  decision,
  reason: '',
  attested: false,
  password: '',
})

/** The pack line's run operator or cleanout person may not sign its release (compared as user ids). */
export function isOwnRun(
  userId: string | null | undefined,
  lot: { operatorId: string | null; cleanoutById: string | null } | null | undefined,
): boolean {
  if (!userId || !lot) return false
  return userId === lot.operatorId || userId === lot.cleanoutById
}

export const NOT_RELEASABLE_REASON = 'This pack lot cannot be released in its present state.'

/** RELEASE is possible from CLOSED, or RELEASED and on hold; never recalled, withdrawn or destroyed (mirrors uc-core). */
export function releasableState(status: string, hold: string): boolean {
  if (hold === 'RECALLED') return false
  return status === 'CLOSED' || (status === 'RELEASED' && hold === 'HELD')
}

export interface ReleaseContext {
  ownRun: boolean
  canRelease: boolean
  /** Omitted = not known yet (treated as releasable; the server checks again). */
  releasable?: boolean
}

/** Why Release is not possible right now, or null. */
export function releaseBlockedReason(ctx: ReleaseContext): string | null {
  if (ctx.releasable === false) return NOT_RELEASABLE_REASON
  if (ctx.ownRun) return OWN_RUN_REASON
  if (!ctx.canRelease) return NOT_GREEN_REASON
  return null
}

export function validateReleaseDecision(d: ReleaseDecisionDraft, ctx: ReleaseContext): ReleaseDecisionProblems {
  const p: ReleaseDecisionProblems = {}
  if (d.decision === 'RELEASE') {
    const blocked = releaseBlockedReason(ctx)
    if (blocked) p.decision = blocked
  }
  if (d.decision === 'HOLD') {
    const r = d.reason.trim()
    if (!r) p.reason = 'Say why the pack lot is held'
    else if (r.length < HOLD_REASON_MIN) p.reason = `At least ${HOLD_REASON_MIN} characters`
    else if (r.length > HOLD_REASON_MAX) p.reason = `At most ${HOLD_REASON_MAX} characters`
  } else if (d.reason.trim().length > HOLD_REASON_MAX) p.reason = `At most ${HOLD_REASON_MAX} characters`
  if (!d.attested) p.attested = 'Tick that you have read the lab report and checked the label'
  if (!d.password) p.password = 'Enter your password to sign'
  return p
}

export const releaseDecisionOk = (p: ReleaseDecisionProblems) => Object.values(p).every((v) => !v)

/** POST /pack-lots/:id/decisions body. Call only when valid. */
export function releaseDecisionBody(d: ReleaseDecisionDraft, reauthToken: string) {
  const reason = d.reason.trim()
  return { decision: d.decision, ...(reason ? { reason } : {}), attested: true as const, reauthToken }
}

/** 409 CHECKLIST_CHANGED { failing: [{ key, detail[] }] } → readable lines. */
export function checklistChanges(failing: unknown): Array<{ key: string; label: string; detail: string[] }> {
  if (!Array.isArray(failing)) return []
  return failing
    .map((f) => (f && typeof f === 'object' ? (f as Record<string, unknown>) : {}))
    .filter((f) => typeof f.key === 'string')
    .map((f) => ({
      key: String(f.key),
      label: checklistLabel(String(f.key)),
      detail: Array.isArray(f.detail) ? f.detail.map(String) : [],
    }))
}
